import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { hydrateSection } from '@theme/collection-product-row';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      if (mutation.attributeName === 'data-error') continue;

      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      ) {
        continue;
      }

      if (
        mutation.attributeName === 'data-intent' ||
        mutation.attributeName === 'data-product-id'
      ) {
        this.dataset.recommendationsPerformed = 'false';
      }

      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });

    if (this.dataset.recommendationsPerformed === 'true') {
      this.#hydrateProductRow();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Hydrates collection-product-row cards inside this element.
   * @returns {Promise<void>}
   */
  async #hydrateProductRow() {
    const row = this.querySelector('collection-product-row');
    if (row instanceof HTMLElement) {
      await hydrateSection(row);
      if (typeof row.refreshPagination === 'function') {
        row.refreshPagination();
      }
    }
  }

  /**
   * Load the product recommendations
   */
  async #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    if (recommendationsPerformed === 'true') {
      await this.#hydrateProductRow();
      return;
    }

    try {
      const result = await this.#fetchCachedRecommendations(productId, sectionId, intent);

      if (!result.success) {
        if (!Shopify.designMode) {
          this.#handleError(new Error(`Server returned ${result.status}`));
        }
        return;
      }

      if (!result.data?.trim().length) {
        this.#handleError(new Error('No recommendations available'));
        return;
      }

      this.dataset.recommendationsPerformed = 'true';
      morphSection(sectionId, result.data, { mode: 'hydration' });
      await this.#hydrateProductRow();
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.#handleError(error instanceof Error ? error : new Error('Recommendations failed'));
    }
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
